import React from 'react';
import { motion } from 'framer-motion';
import { Brain, TrendingUp, Heart, Activity } from 'lucide-react';
import { HealthMetrics } from '../../../types/health';

interface WorkoutInsightsProps {
  workout: {
    targetStrain: number;
    duration: number;
    type: string;
  };
  metrics: HealthMetrics;
}

export function WorkoutInsights({ workout, metrics }: WorkoutInsightsProps) {
  const insights = [
    {
      title: 'Strain Impact',
      description: `This workout will add ${workout.targetStrain.toFixed(1)} strain to your daily total of ${metrics.strain.current.toFixed(1)}.`,
      icon: <Activity className="w-5 h-5 text-primary" />,
      color: 'from-primary/20 to-purple-500/20'
    },
    {
      title: 'Recovery Status',
      description: `Your ${metrics.recovery}% recovery indicates ${metrics.recovery > 70 ? 'optimal conditions' : 'caution needed'} for this intensity.`,
      icon: <Heart className="w-5 h-5 text-success" />,
      color: 'from-success/20 to-green-500/20'
    },
    {
      title: 'Performance Potential',
      description: `Based on your readiness score of ${metrics.readiness}%, you're ${metrics.readiness > 80 ? 'primed for' : 'capable of'} this workout.`,
      icon: <TrendingUp className="w-5 h-5 text-blue-500" />,
      color: 'from-blue-500/20 to-cyan-500/20'
    }
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Brain className="w-5 h-5 text-primary" />
        <h2 className="font-semibold">AI Insights</h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {insights.map((insight, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="relative overflow-hidden rounded-lg p-4"
          >
            <div className={`absolute inset-0 bg-gradient-to-br ${insight.color}`} />
            <div className="relative space-y-3">
              <div className="flex items-center gap-2">
                {insight.icon}
                <h4 className="font-medium">{insight.title}</h4>
              </div>
              <p className="text-sm text-muted-foreground">
                {insight.description}
              </p>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}